'use client';

import Box from '@mui/material/Box';
import { animated, to, type SpringValue } from 'react-spring';
import { GRUND, djup, hexTillRgba, ljus, type Kombonamn } from './rörelse-konfig';
import type { IFärgKombo } from 'src/theme/NyttFärgtema';

const AnimeradDiv = animated('div');

export type Flödesnod = { id: string; etikett: string; under?: string };

type Props = {
    noder: Flödesnod[];
    kombo: Kombonamn | IFärgKombo;
    riktning?: 'vågrät' | 'lodrät' | 'ring';
    /** Diskret: noder till och med detta index är tända. */
    aktivIndex?: number;
    /** Kontinuerligt: ett enda värde fanar ut till alla noder via .to(). */
    framsteg?: SpringValue<number>;
    /** Radie i pixlar för ring-varianten. */
    radie?: number;
    etikett: string;
};

/**
 * Noder med kanter emellan. Arbetshästen — den bär argumentet på fyra av fem sidor,
 * i olika orientering, vilket är en stor del av varför sidorna kan dela primitiver och
 * ändå läsa olika.
 *
 * Kanterna är vanliga element som skalas med scaleX/scaleY, inte SVG-strokeDashoffset:
 * det senare är inte kompositoraccelererat och rycker så fort spåret blir långt.
 */
export function Flödesspår({
    noder,
    kombo,
    riktning = 'vågrät',
    aktivIndex,
    framsteg,
    radie = 168,
    etikett
}: Props) {
    const antal = noder.length;

    const tändning = (i: number) => {
        const tröskel = antal <= 1 ? 0 : i / antal;
        if (framsteg) {
            return {
                opacity: framsteg.to((p) => (p >= tröskel ? 1 : 0.34)),
                scale: framsteg.to((p) => (p >= tröskel ? 1 : 0.86))
            };
        }
        const tänd = aktivIndex === undefined || i <= aktivIndex;
        return { opacity: tänd ? 1 : 0.34, scale: tänd ? 1 : 0.86 };
    };

    const kantSkala = (i: number) => {
        const tröskel = antal <= 1 ? 0 : (i + 0.5) / antal;
        if (framsteg) return framsteg.to((p) => (p >= tröskel ? 1 : 0));
        return aktivIndex !== undefined && i < aktivIndex ? 1 : 0;
    };

    if (riktning === 'ring') {
        return (
            <Box
                role="list"
                aria-label={etikett}
                sx={{
                    position: 'relative',
                    width: radie * 2 + 120,
                    height: radie * 2 + 120,
                    maxWidth: '100%',
                    mx: 'auto'
                }}
            >
                {noder.map((nod, i) => {
                    const vinkel = (i / antal) * 360;
                    const stil = tändning(i);
                    return (
                        <AnimeradDiv
                            key={nod.id}
                            role="listitem"
                            style={{
                                position: 'absolute',
                                left: '50%',
                                top: '50%',
                                opacity: stil.opacity,
                                transform: to([stil.scale], (s) =>
                                    `translate(-50%, -50%) rotate(${vinkel}deg) translateY(${-radie}px) rotate(${-vinkel}deg) scale(${s})`
                                )
                            }}
                        >
                            <NodKort nod={nod} kombo={kombo} kompakt />
                        </AnimeradDiv>
                    );
                })}
            </Box>
        );
    }

    const lodrät = riktning === 'lodrät';

    return (
        <Box
            role="list"
            aria-label={etikett}
            sx={{
                display: 'flex',
                flexDirection: lodrät ? 'column' : 'row',
                alignItems: lodrät ? 'flex-start' : 'center',
                gap: 0,
                flexWrap: lodrät ? 'nowrap' : 'wrap'
            }}
        >
            {noder.map((nod, i) => {
                const stil = tändning(i);
                return (
                    <Box
                        key={nod.id}
                        sx={{
                            display: 'flex',
                            flexDirection: lodrät ? 'column' : 'row',
                            alignItems: lodrät ? 'flex-start' : 'center'
                        }}
                    >
                        <AnimeradDiv
                            role="listitem"
                            style={{
                                opacity: stil.opacity,
                                transform: to([stil.scale], (s) => `scale(${s})`)
                            }}
                        >
                            <NodKort nod={nod} kombo={kombo} />
                        </AnimeradDiv>

                        {i < antal - 1 ? (
                            <AnimeradDiv
                                aria-hidden
                                style={{
                                    transform: to([kantSkala(i)], (s) =>
                                        lodrät ? `scaleY(${s})` : `scaleX(${s})`
                                    ),
                                    transformOrigin: lodrät ? 'top' : 'left',
                                    width: lodrät ? 1 : 56,
                                    height: lodrät ? 34 : 1,
                                    marginLeft: lodrät ? 19 : 10,
                                    marginRight: lodrät ? 0 : 10,
                                    background: ljus(kombo)
                                }}
                            />
                        ) : null}
                    </Box>
                );
            })}
        </Box>
    );
}

function NodKort({
    nod,
    kombo,
    kompakt
}: {
    nod: Flödesnod;
    kombo: Kombonamn | IFärgKombo;
    kompakt?: boolean;
}) {
    return (
        <Box
            sx={{
                px: kompakt ? 1.5 : 2,
                py: kompakt ? 0.85 : 1.25,
                minWidth: kompakt ? 96 : 120,
                borderRadius: 1.5,
                textAlign: 'center',
                bgcolor: hexTillRgba(djup(kombo), 0.32),
                border: `1px solid ${hexTillRgba(ljus(kombo), 0.4)}`,
                backdropFilter: 'blur(6px)'
            }}
        >
            <Box sx={{ fontSize: kompakt ? 12.5 : 14, fontWeight: 600, color: GRUND.text, lineHeight: 1.3 }}>
                {nod.etikett}
            </Box>
            {nod.under ? (
                <Box sx={{ fontSize: 11.5, color: GRUND.dämpad, mt: 0.25 }}>{nod.under}</Box>
            ) : null}
        </Box>
    );
}
